import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import loyaltyService from '../../services/loyaltyService';
import type { LoyaltySettings, LoyaltyStats, PointsLedgerEntry, Client } from '../../types';

// ─── Thunks ──────────────────────────────────────────────────────────────────
export const fetchLoyaltySettings = createAsyncThunk('loyalty/fetchSettings', async (_, { rejectWithValue }) => {
  try { const r = await loyaltyService.getSettings(); return r.data!; }
  catch (e: any) { return rejectWithValue(e.response?.data?.message ?? e.message); }
});

export const updateLoyaltySettings = createAsyncThunk('loyalty/updateSettings', async (data: Partial<LoyaltySettings>, { rejectWithValue }) => {
  try { const r = await loyaltyService.updateSettings(data); return r.data!; }
  catch (e: any) { return rejectWithValue(e.response?.data?.message ?? e.message); }
});

export const fetchLoyaltyStats = createAsyncThunk('loyalty/fetchStats', async (_, { rejectWithValue }) => {
  try { const r = await loyaltyService.getStats(); return r.data!; }
  catch (e: any) { return rejectWithValue(e.response?.data?.message ?? e.message); }
});

export const fetchLoyaltyClients = createAsyncThunk('loyalty/fetchClients', async (params: Record<string,string> = {}, { rejectWithValue }) => {
  try { const r = await loyaltyService.getClients(params); return { data: r.data!, total: r.total ?? 0 }; }
  catch (e: any) { return rejectWithValue(e.response?.data?.message ?? e.message); }
});

export const fetchClientHistory = createAsyncThunk('loyalty/fetchHistory', async (clientId: string, { rejectWithValue }) => {
  try { const r = await loyaltyService.getHistory(clientId); return r.data!; }
  catch (e: any) { return rejectWithValue(e.response?.data?.message ?? e.message); }
});

export const adjustClientPoints = createAsyncThunk('loyalty/adjust', async ({ clientId, data }: { clientId: string; data: { points: number; description: string } }, { rejectWithValue }) => {
  try { const r = await loyaltyService.adjustPoints(clientId, data); return r.data!; }
  catch (e: any) { return rejectWithValue(e.response?.data?.message ?? e.message); }
});

// ─── State ───────────────────────────────────────────────────────────────────
interface LoyaltyState {
  settings: LoyaltySettings | null;
  stats:    LoyaltyStats | null;
  clients:  Client[];
  total:    number;
  history:  PointsLedgerEntry[];
  loading:  boolean;
  error:    string | null;
}

const initial: LoyaltyState = {
  settings: null, stats: null, clients: [], total: 0, history: [], loading: false, error: null,
};

// ─── Slice ───────────────────────────────────────────────────────────────────
const loyaltySlice = createSlice({
  name: 'loyalty',
  initialState: initial,
  reducers: {},
  extraReducers: builder => {
    const load = (s: LoyaltyState) => { s.loading = true; s.error = null; };
    const fail = (s: LoyaltyState, a: any) => { s.loading = false; s.error = a.payload as string; };

    builder
      .addCase(fetchLoyaltySettings.pending,   load)
      .addCase(fetchLoyaltySettings.rejected,  fail)
      .addCase(fetchLoyaltySettings.fulfilled, (s, a) => { s.loading = false; s.settings = a.payload; })

      .addCase(updateLoyaltySettings.fulfilled, (s, a) => { s.settings = a.payload; })

      .addCase(fetchLoyaltyStats.fulfilled, (s, a) => { s.stats = a.payload; })

      .addCase(fetchLoyaltyClients.pending,   load)
      .addCase(fetchLoyaltyClients.rejected,  fail)
      .addCase(fetchLoyaltyClients.fulfilled, (s, a) => { s.loading = false; s.clients = a.payload.data; s.total = a.payload.total; })

      .addCase(fetchClientHistory.fulfilled, (s, a) => { s.history = a.payload; })

      .addCase(adjustClientPoints.fulfilled, (s, a) => {
        const idx = s.clients.findIndex(c => c._id === a.payload._id);
        if (idx >= 0) s.clients[idx] = a.payload;
      });
  },
});

export default loyaltySlice.reducer;
